import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import { Tooltip } from "@mui/material";
import TranscriptViewer from "./TranscriptViewer";
import FileNoteViewer from "./FileNoteViewer";
import RawTextViewer from "./RawTextViewer";
import FactFindViewer from "./FactFindViewer";

interface FilePreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: any;
}

export default function FilePreviewModal({ isOpen, onClose, item }: FilePreviewModalProps) {
    if (!item) return null;

    const mimeType = item.file?.mimeType || "";
    const entityType = item.prospoContext?.entityType || "";
    const fileUrl = item.fileUrl;

    const handleDownload = () => {
        if (fileUrl) window.open(fileUrl, "_blank");
    };

    // --- THE SMART ROUTER ---
    const renderPreviewContent = () => {
        // 0. FACT FIND — Render as a styled HTML report (must come before the JSON/text fallback)
        if (entityType === "FACT_FIND") {
            return <FactFindViewer item={item} />;
        }

        // DOCX files must always go to the Office viewer, regardless of entityType
        const isDocx =
            mimeType.includes("wordprocessingml") ||
            item.name?.toLowerCase().endsWith(".docx");

        // 1. TRANSCRIPTS (Strict Matching: Must be an actual DB transcript entity, OR explicitly named 'TRANSCRIPTION')
        //    Skip if the file is a DOCX — those fall through to the Office viewer below.
        const isActualTranscript =
            !isDocx && (
                entityType === "TRANSCRIPT" ||
                (item.name?.toUpperCase().includes("TRANSCRIPTION") && mimeType.includes("json"))
            );

        if (isActualTranscript) {
            return <TranscriptViewer item={item} />;
        }

        // 2. FILE NOTES / SUMMARIES — skip DOCX file notes, they go to the Office viewer.
        if (!isDocx && (entityType === "FILE_NOTE" || mimeType === "text/html" || item.name?.toLowerCase().endsWith(".html"))) {
            return <FileNoteViewer item={item} />;
        }

            // 3. RAW TEXT, CODE, VTT, and GENERIC JSON (Must go AFTER Transcripts & HTML)
        //    DOCX files are excluded even if the DB has a stale mimeType like "text/html"
        const isTextFile =
            !isDocx && (
                mimeType.startsWith("text/") ||
                mimeType.includes("json") ||
                !!item.name?.toLowerCase().match(/\.(vtt|json|txt|md|csv|log)$/)
            );

        // Allow inline-data files (e.g. FactFind stored in DB) to reach the viewer even without a URL
        if (isTextFile || (!isDocx && item.data)) {
            return <RawTextViewer item={item} />;
        }

        // If it's a physical file but we failed to get the URL
        if (!fileUrl) {
            return (
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="error">File URL could not be generated. The file might be missing from storage.</Typography>
                </Box>
            );
        }

        // 4. IMAGES
        if (mimeType.startsWith("image/")) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#e0e0e0" }}>
                    <img src={fileUrl} alt={item.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </Box>
            );
        }

        // 4. PDFs
        if (mimeType === "application/pdf") {
            // Use Google Docs Viewer to bypass SharePoint's forced download headers
            const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;

            return (
                <Box sx={{ width: "100%", height: "100%", position: "relative", bgcolor: "#525659" }}>
                    <iframe
                        src={googleViewerUrl}
                        title={item.name}
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                    />
                    <Typography variant="caption" sx={{ position: "absolute", bottom: 8, right: 16, color: "#fff", zIndex: 0 }}>
                        If the preview doesn't load, please use the download button above.
                    </Typography>
                </Box>
            );
        }

        // 5. AUDIO / VIDEO (Native HTML5 Player)
        if (mimeType.startsWith("video/") || mimeType.startsWith("audio/")) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#000" }}>
                    <video controls controlsList="nodownload" style={{ maxWidth: "100%", maxHeight: "100%" }}>
                        <source src={fileUrl} type={mimeType} />
                        Your browser does not support the video tag.
                    </video>
                </Box>
            );
        }

        // 6. MICROSOFT OFFICE DOCUMENTS (Word, Excel, PowerPoint)
        const isOfficeDoc =
            mimeType.includes("wordprocessingml") ||
            mimeType.includes("presentationml") ||
            mimeType.includes("spreadsheetml") ||
            mimeType.includes("msword") ||
            mimeType.includes("ms-excel") ||
            mimeType.includes("ms-powerpoint") ||
            // FALLBACK: Check file extension in case the DB mimeType is empty
            item.name?.toLowerCase().match(/\.(docx|doc|pptx|ppt|xlsx|xls)$/);

        if (isOfficeDoc) {
            // 🚀 The Google Docs Viewer is 10x more reliable for Azure SAS URLs than the Microsoft Viewer
            const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;

            return (
                <Box sx={{ width: "100%", height: "100%", position: "relative", bgcolor: "#f5f5f5" }}>
                    <iframe
                        src={googleViewerUrl}
                        title={item.name}
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                        // Add a title to help with accessibility
                        aria-label="Document Preview"
                    />
                    {/* Quick fallback in case Google Viewer is blocked by a corporate firewall */}
                    <Typography variant="caption" sx={{ position: "absolute", bottom: 8, right: 16, color: "text.secondary", zIndex: 0 }}>
                        If the preview doesn't load, please use the download button above.
                    </Typography>
                </Box>
            );
        }

        // 7. UNSUPPORTED FILES (Fallback)
        return (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#f5f5f5" }}>
                <DescriptionIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6">No preview available</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    This file type cannot be previewed directly in the browser.
                </Typography>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                    Download to View
                </Button>
            </Box>
        );
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { height: "90vh", display: "flex", flexDirection: "column" } }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: 1, borderColor: "divider", py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {entityType === "FACT_FIND"
                        ? (item.name || "").replace(/\.(json|pdf)$/, ".pdf")
                        : item.name}
                </Typography>
                <Box>
                    {fileUrl && (
                        <Tooltip title="Download Original File">
                            <IconButton onClick={handleDownload} sx={{ mr: 1 }}>
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, flexGrow: 1, overflow: "hidden" }}>
                {renderPreviewContent()}
            </DialogContent>
        </Dialog>
    );
}